/**
 * <TutorLivesListPage> — « Mes prochains lives », la semaine du masteur (page).
 *
 * Ce que « Toute la semaine » ouvre depuis l'accueil (`<TutorTodaySection onOpenWeek>`). Le legacy
 * en avait deux formes, la même liste de cartes `TutorLiveCardComponent` : la modale « VOIR TOUS »
 * de la carte « Mes prochains lives » de l'accueil (`TutorNextEventsModal`, la seule atteinte), et
 * l'écran `next-lives` (`TutorNextLivesListScreen`, vers lequel plus rien ne navigue).
 *
 * La page est « Tous mes lives » du parent et de l'élève, à la voix du masteur : les séances rangées
 * par jour, une carte-liste par jour, une ligne par séance — l'anatomie de toute séance
 * (`<SessionRow>`), avec ce qui est au masteur, la même que sur son accueil (`tutorSessionAnatomy`) :
 * rien à la place de la capsule du tuteur, le type de cours et l'école sans « avec Camille », puis
 * ses élèves un par un, par leur nom, avec leur classe, leurs matières et ce qui les distingue À
 * CETTE date (`<TutorSessionRoster>` — l'ancien `StudentsListStringForOccurrence`) : la liste de la
 * semaine est celle où le masteur vérifie qui vient.
 *
 * **Toucher une séance** fait ce que faisait son bouton « Gérer les RDV » (`onSelectSession`) : une
 * séance qui porte déjà une demande (retard signalé, remplacement demandé ou accepté) ouvre sa boîte,
 * les autres partent vers « Gérer une séance ». La boîte est celle de l'accueil, contrôlée de la même
 * façon (`sessionNotice`, comme `<TutorHomePage>`) : elle dit une demande, puis le refus d'une
 * annulation (« Impossible d'annuler »), une réponse du serveur.
 *
 * **Chaque séance porte son « GO ! »** (`<TutorSessionGo>`, comme les anciennes cartes de la liste) :
 * plein sous « Aujourd'hui », il ouvre le précall de la séance ; les autres jours, secondaire, et
 * l'écran ouvre « Le live n'est accessible que le jour même » (`notTodaySession`,
 * `<LiveAccessDialog>`), qui dit quel jour revenir.
 */
import React from "react"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { CardList } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import { RefreshButton } from "../../molecules/RefreshButton"
import { SessionRow } from "../../molecules/SessionRow"
import { Snackbar } from "../../molecules/Snackbar"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { TutorSessionNoticeDialog, type TutorSessionNotice } from "../../templates/TutorSessionNoticeDialog"
import { LiveAccessDialog } from "../../templates/LiveAccessDialog"
import { TutorSessionGo, TutorSessionRowSkeleton, tutorSessionAnatomy } from "../../templates/TutorTodaySection"
import { type UITutorWeekGroup, type UITutorWeekSession } from "../../../ports/primary/ui/types/tutorLives"
import { LiveSessionsListSkeleton } from "../LiveSessionsListPage/LiveSessionsListSkeleton"

const TEXTS = {
    title: "Mes prochains lives",
    empty: "Aucun live prévu pour l'instant.",
}

const noop = () => {}

export type TutorLivesListPageProps = {
    groups: UITutorWeekGroup[]
    /** Premier chargement : le squelette. */
    loading?: boolean
    /** Une relecture, les séances déjà à l'écran : seule la flèche de l'en-tête tourne. */
    refreshing?: boolean
    error?: unknown
    onRetry?: () => void
    onClose?: () => void
    onRefresh?: () => void
    /** « Rafraîchir » refusé, dit en bas de page (`<Snackbar>`) : le message du serveur. `null` : rien. */
    notice?: string | null
    onDismissNotice?: () => void
    /** Une séance touchée : sa boîte si elle porte une demande, sinon « Gérer une séance ». */
    onSelectSession?: (session: UITutorWeekSession) => void
    /** « GO ! » d'une séance : le précall le jour du live, sinon la boîte « accessible le jour même ». Absent : pas de bouton. */
    onJoinSession?: (session: UITutorWeekSession) => void
    /** La séance dont le « GO ! » a été pressé un autre jour que le sien : la boîte qui le dit. `null` : fermée. */
    notTodaySession?: UITutorWeekSession | null
    onCloseNotToday?: () => void
    /** La boîte d'une séance qui porte déjà une demande, ou du refus de l'annuler. `null` : fermée. */
    sessionNotice?: { notice: TutorSessionNotice; session: UITutorWeekSession; lateMinutes?: number } | null
    /** « Annuler la demande » d'un retard ou d'un remplacement pas encore accepté. */
    onCancelSessionRequest?: () => void
    /** « Contacter le support » de la boîte du refus. */
    onContactSupport?: () => void
    onCloseSessionNotice?: () => void
    testID?: string
}

export function TutorLivesListPage(props: TutorLivesListPageProps) {
    const {
        groups,
        loading = false,
        refreshing = false,
        error,
        onRetry,
        onClose,
        onRefresh,
        notice = null,
        onDismissNotice = noop,
        onSelectSession,
        onJoinSession,
        notTodaySession = null,
        onCloseNotToday = noop,
        sessionNotice = null,
        onCancelSessionRequest,
        onContactSupport,
        onCloseSessionNotice = noop,
        testID,
    } = props

    const header = (
        <Header
            title={TEXTS.title}
            left={<IconButton iconName="times" appearance="transparent" iconColor="primary" onPress={onClose} />}
            right={onRefresh != null ? <RefreshButton refreshing={refreshing} onRefresh={onRefresh} /> : undefined}
        />
    )

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<LiveSessionsListSkeleton row={<TutorSessionRowSkeleton timeOnly />} />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {groups.length === 0 ? (
                <EmptyState title={TEXTS.empty} iconName="calendar" />
            ) : (
                <Stack gap="6">
                    {groups.map((group) => (
                        <Stack key={group.id} gap="3">
                            <SectionLabel color={group.isToday ? "success" : "text-muted"}>{group.title}</SectionLabel>
                            <CardList>
                                {group.sessions.map((session) => (
                                    <SessionRow
                                        key={session.id}
                                        {...tutorSessionAnatomy(session)}
                                        timeOnly
                                        onPress={() => onSelectSession?.(session)}
                                        trailing={
                                            onJoinSession != null ? (
                                                <TutorSessionGo
                                                    date={session.date}
                                                    onPress={() => onJoinSession(session)}
                                                />
                                            ) : undefined
                                        }
                                    />
                                ))}
                            </CardList>
                        </Stack>
                    ))}
                </Stack>
            )}
            <TutorSessionNoticeDialog
                notice={sessionNotice?.notice ?? null}
                session={sessionNotice?.session ?? null}
                lateMinutes={sessionNotice?.lateMinutes}
                onCancelRequest={onCancelSessionRequest}
                onContactSupport={onContactSupport}
                onClose={onCloseSessionNotice}
            />
            <LiveAccessDialog
                notice={notTodaySession != null ? "not_today" : null}
                session={notTodaySession}
                onClose={onCloseNotToday}
            />
            <Snackbar visible={notice != null} message={notice ?? ""} tone="danger" onDismiss={onDismissNotice} />
        </PageTemplate>
    )
}
